"use client";

import { Button } from "@/components/atoms/Button";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { RootState } from "@/store/store";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";

interface HeroSlide {
  id?: string | number;
  image?: string;
  alt_image?: string;
  /** Optional; falls back to `image` when empty */
  mobile_image?: string;
  mobile_alt_image?: string;
  title_line1?: string;
  title_line1_accent?: string;
  title_line2?: string;
  /** When true, title line 2 flows in the same `h1` as line 1 (continuous headline). */
  merge_title_lines?: boolean;
  subtitle?: string;
  /** Per-slide title colours (hex). Empty = default theme */
  title_line1_accent_color?: string;
  title_line1_color?: string;
  title_line2_color?: string;
  subtitle_color?: string;
  hero_right_image?: string;
  alt_hero?: string;
  programs_json?: string;
  /** Per-slide accreditation — FILE uploads (order 1 → 3) */
  accreditation_logo_1?: string;
  accreditation_logo_1_alt?: string;
  accreditation_logo_2?: string;
  accreditation_logo_2_alt?: string;
  accreditation_logo_3?: string;
  accreditation_logo_3_alt?: string;
  /** Legacy only — used if no uploaded logos on the slide */
  accreditation_logos_json?: string;
  priority?: number;
  status?: boolean;
}

interface AccLogo {
  id?: string | number;
  image?: string;
  alt_image?: string;
  priority?: number;
  status?: boolean;
}

interface HeroCarouselData {
  metadata?: HeroSlide[];
  metadata_1?: AccLogo[];
  slide_frequency?: number;
  hero_section_height?: number;
  hero_container_max_width?: number;
  background_color?: string;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  apply_now_show?: boolean;
  hero_info_bar_text?: string;
  hero_info_bar_show?: boolean;
  download_brochure_text?: string;
  download_brochure_link?: string;
  download_brochure_is_cta?: boolean;
  download_brochure_show?: boolean;
  application_deadline_text?: string;
  application_deadline_show?: boolean;
  button_color?: string;
  apply_now_color?: string;
  carousel_color?: string;
  status?: boolean;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function parsePrograms(
  raw: string | undefined,
): { label: string; href: string }[] {
  if (!raw?.trim()) return [];
  try {
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr
      .filter(
        (x): x is { label: string; href: string } =>
          !!x &&
          typeof x === "object" &&
          typeof (x as { label?: string }).label === "string" &&
          typeof (x as { href?: string }).href === "string",
      )
      .map((x) => ({
        label: x.label,
        href: normalizeHref(x.href),
      }));
  } catch {
    return [];
  }
}

const ACC_LOGO_SLOT_COUNT = 3;

function parseAccLogos(raw: string | undefined): AccLogo[] {
  if (!raw?.trim()) return [];
  try {
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr
      .filter(
        (x): x is AccLogo =>
          !!x &&
          typeof x === "object" &&
          typeof (x as { image?: string }).image === "string" &&
          (x as { image: string }).image.length > 0,
      )
      .map((x) => ({
        id: x.id,
        image: x.image,
        alt_image: x.alt_image,
        priority: x.priority,
        status: x.status,
      }))
      .filter((a) => a.status !== false)
      .sort(
        (a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0),
      );
  } catch {
    return [];
  }
}

/** Uploaded logos on the slide; falls back to legacy JSON if none */
function accLogosFromSlide(slide: HeroSlide): AccLogo[] {
  const fromFiles: AccLogo[] = [];
  for (let i = 1; i <= ACC_LOGO_SLOT_COUNT; i++) {
    const url = slide[`accreditation_logo_${i}` as keyof HeroSlide] as
      | string
      | undefined;
    const alt = slide[`accreditation_logo_${i}_alt` as keyof HeroSlide] as
      | string
      | undefined;
    if (typeof url === "string" && url.trim()) {
      fromFiles.push({
        image: url.trim(),
        alt_image: typeof alt === "string" ? alt.trim() : "",
        priority: i,
        status: true,
      });
    }
  }
  if (fromFiles.length > 0) return fromFiles;
  return parseAccLogos(slide.accreditation_logos_json);
}

/**
 * Mobile readability swap: `#DB5803` (orange) is low-contrast on the mobile
 * dark scrim, so render it as `#FFC600` on small screens. Desktop keeps the
 * original colour. Returns the colour to use for the mobile (base) rendering.
 */
function mobileTitleColor(color?: string): string | undefined {
  if (!color) return undefined;
  return color.trim().toLowerCase() === "#db5803" ? "#FFC600" : color;
}

/** Programme links — match IIT `HomeBanner` link stack; #171719 / #FFFFFF / gray-200 */
const programLinkClass =
  "flex items-center justify-center w-fit min-[1024px]:h-9 text-heading-7 min-[1024px]:text-heading-6 !font-semibold text-white min-[1024px]:!text-[#171719] my-2 min-[1024px]:px-2.5 min-[1024px]:py-2 rounded-md underline min-[1024px]:no-underline min-[1024px]:border min-[1024px]:border-gray-200 min-[1024px]:bg-[#FFFFFF] cursor-pointer hover:shadow-md hover:border-transparent whitespace-nowrap";

const HeroCarousel = (props: { data?: HeroCarouselData }) => {
  const { data: sectionData } = props;
  const dispatch = useDispatch();
  const [index, setIndex] = useState(0);
  /** Bound course for this page (set by NewPage from API `data.course`). */
  const boundCourse = useSelector(
    (state: RootState) => state.globalslice?.course,
  );
  /** Full courses list with admin-mapped brochures (loaded for the lead form). */
  const leadCourses = useSelector(
    (state: RootState) => state.leaddrop?.courses ?? [],
  );
  /** Course bound at the widget level on Program Enrollments pages (erp_unicode / id). */
  const enrollmentBoundCourse = useSelector(
    (state: RootState) => state.leaddrop?.enrollmentBoundCourseSelectValue ?? null,
  );

  /**
   * Course-mapped brochure. The bound-course object from the API often omits the
   * uploaded brochure, so resolve it from the courses list — by the page course
   * (erp_unicode / pok) or the enrollment-page binding (erp_unicode / id) — and
   * fall back to any brochure on the bound course itself.
   */
  const brochureFromCourse = useMemo(() => {
    const direct = String(boundCourse?.brochure || "").trim();
    if (direct) return direct;
    // Keys this tenant may use to identify the bound course. On IIT KGP pages
    // the enrollment binding is the program_name, so match on that too.
    const keys = [
      boundCourse?.erp_unicode,
      boundCourse?.pok,
      boundCourse?.program_name,
      enrollmentBoundCourse,
    ]
      .map((k) => String(k || "").trim())
      .filter(Boolean);
    if (!keys.length) return "";
    const match = leadCourses.find((c) =>
      keys.some(
        (k) =>
          c.erp_unicode === k ||
          c.program_name === k ||
          c.pok === k ||
          String(c.id ?? "") === k,
      ),
    );
    return String(match?.brochure || "").trim();
  }, [boundCourse, enrollmentBoundCourse, leadCourses]);

  const {
    metadata = [],
    metadata_1 = [],
    slide_frequency = 7000,
    hero_section_height = 564,
    hero_container_max_width = 1114,
    background_color = "#291973",
    hero_info_bar_text = "",
    hero_info_bar_show = true,
    apply_now_show = true,
    apply_now_text = "",
    apply_now_link = "",
    download_brochure_text = "Download Brochure",
    download_brochure_link = "",
    download_brochure_show = true,
    application_deadline_text = "",
    application_deadline_show = true,
    button_color = "#DB5803",
    apply_now_color = "#ffffff",
    carousel_color = "#DB5803",
    status = true,
  } = sectionData || {};

  /** Default on: persisted API wins after migrate. Keys missing → lead popup until toggles explicitly off + URL. */
  const bannerRaw = sectionData as Record<string, unknown> | undefined;
  const applyNowIsCta = isCmsShowFlagOn(
    bannerRaw?.apply_now_is_cta ?? bannerRaw?.applyNowIsCta,
    true,
  );
  const brochureIsCta = isCmsShowFlagOn(
    bannerRaw?.download_brochure_is_cta ??
      bannerRaw?.downloadBrochureIsCta,
    true,
  );

  /**
   * Right vertical accent strip colour. When the admin clears this field
   * (empty value) we render NO strip/band on the right — the slide keeps its
   * full-width background and the hero image straddles a plain background.
   */
  const accentStripColor = String(background_color ?? "").trim();
  const hasAccentStrip = accentStripColor.length > 0;
  const contentMaxPx = Number(hero_container_max_width) || 1114;
  /** Same as design: `calc(50% - 557px)` when max width is 1114 — right gutter to viewport edge */
  const rightGutterHalfPx = contentMaxPx / 2;

  const slides: HeroSlide[] = useMemo(() => {
    if (!Array.isArray(metadata)) return [];
    return [...metadata]
      .filter(
        (s: HeroSlide) =>
          s.status !== false &&
          (String(s.image ?? "").trim() || String(s.mobile_image ?? "").trim()),
      )
      .sort(
        (a: HeroSlide, b: HeroSlide) =>
          (a.priority ?? 0) - (b.priority ?? 0),
      );
  }, [metadata]);

  /** Legacy global logos (Banner 3 second table removed — used only if no slide has JSON) */
  const accLogosGlobal: AccLogo[] = useMemo(() => {
    if (!Array.isArray(metadata_1)) return [];
    return [...metadata_1]
      .filter((a: AccLogo) => a.status !== false && a.image)
      .sort(
        (a: AccLogo, b: AccLogo) => (a.priority ?? 0) - (b.priority ?? 0),
      );
  }, [metadata_1]);

  const anySlideHasOwnAcc = useMemo(
    () => slides.some((s) => accLogosFromSlide(s).length > 0),
    [slides],
  );

  const n = slides.length;
  const bgSlide = n ? slides[Math.min(index, n - 1)] : null;

  useEffect(() => {
    setIndex((i) => (n ? Math.min(i, n - 1) : 0));
  }, [n]);

  const tick = useCallback(() => {
    if (n <= 1) return;
    setIndex((i) => (i + 1) % n);
  }, [n]);

  useEffect(() => {
    if (!status || n <= 1 || !slide_frequency) return undefined;
    const id = window.setInterval(tick, Number(slide_frequency));
    return () => window.clearInterval(id);
  }, [status, n, slide_frequency, tick]);

  const bgDesktopSrc = String(bgSlide?.image ?? "").trim();
  const bgMobileSrc = String(bgSlide?.mobile_image ?? "").trim();

  const brochureHref = download_brochure_link?.trim()
    ? normalizeHref(download_brochure_link)
    : "";

  /** Course-mapped brochure wins over the admin URL for the direct-download link. */
  const directBrochureHref = brochureFromCourse
    ? normalizeHref(brochureFromCourse)
    : brochureHref;

  const ctaHref = apply_now_link?.trim()
    ? normalizeHref(apply_now_link)
    : "";

  /** CTA toggle on, or no URL configured → lead popup */
  const primaryOpensLeadPopup = applyNowIsCta || !ctaHref;
  const brochureOpensLeadPopup = brochureIsCta || !brochureHref;

  const showPrimaryCta =
    isCmsShowFlagOn(apply_now_show) && Boolean(apply_now_text?.trim());

  const showHeroInfoBar =
    isCmsShowFlagOn(hero_info_bar_show) &&
    Boolean(hero_info_bar_text?.trim());

  const showDownloadBrochure =
    isCmsShowFlagOn(download_brochure_show) &&
    Boolean(download_brochure_text?.trim());

  const showApplicationDeadline =
    isCmsShowFlagOn(application_deadline_show) &&
    Boolean(application_deadline_text?.trim());

  const showActionRow =
    showPrimaryCta || showDownloadBrochure || showApplicationDeadline;

  /**
   * Minimal centred banner: title + accreditation only (no action row and no
   * slide carries programme buttons). On mobile these slides use the light
   * desktop tint (#e5e5f98c) instead of the dark photo scrim, and the headline
   * renders in its desktop colours at 36px so it stays readable on the light
   * background — matching production. Everything else keeps the dark scrim.
   */
  const mobileLight =
    !showActionRow &&
    slides.every((s) => parsePrograms(s.programs_json).length === 0);
  /**
   * Mobile title colour. Near-black brand ink (#171719) is unreadable over the
   * banner on small screens, so it always renders white on mobile (desktop keeps
   * it). Otherwise the light tint keeps the real (desktop) colour, while the dark
   * scrim applies the legibility swap.
   */
  const resolveMobileTitleColor = (c?: string) => {
    if (c && c.trim().toLowerCase() === "#171719") return "#FFFFFF";
    return mobileLight ? c : mobileTitleColor(c);
  };
  /** Headline default colours: desktop colours on the light tint, scrim colours otherwise. */
  const accentDefaultCls = mobileLight
    ? "text-primary"
    : "text-yellow-accent min-[1024px]:text-primary";
  const bodyDefaultCls = mobileLight
    ? "text-black"
    : "text-white min-[1024px]:text-black";
  const titleMobileSize = mobileLight ? "text-[36px]" : "text-[24px]";

  const handleApplyNowClick = useCallback(() => {
    ctaClick({
      section: "banner_4",
      component: "hero_apply_now",
      label: apply_now_text?.trim() || "apply_now",
      step: "1",
      entity: "banner_4",
      parentEntity: "banner_4",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [apply_now_text, dispatch]);

  const handleDownloadBrochure = useCallback(() => {
    ctaClick({
      section: "banner_4",
      component: "download_brochure_button",
      label: download_brochure_text,
      step: "1",
      entity: "banner_4",
      parentEntity: "banner_4",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: brochureFromCourse || brochureHref || "",
      }),
    );
  }, [brochureFromCourse, brochureHref, dispatch, download_brochure_text]);

  if (!status || !n || !bgSlide || (!bgDesktopSrc && !bgMobileSrc)) {
    return null;
  }

  return (
    <section
      className="relative isolate flex min-h-0 w-full min-w-0 flex-col self-stretch overflow-x-hidden overflow-y-visible min-[1024px]:overflow-x-clip sm:h-[564px]"
      style={{
        // height: hero_section_height,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {/*
        Do NOT use negative z-index on the bg image: it paints *below* the section's
        background layer, so transparent section shows <main class="bg-gray-1"> (flat gray).
        Use isolate + non-negative z layers instead.
      */}
      <div className="absolute inset-0 z-0">
        {/*
          Two stacked heroes (mobile/desktop are different source files, so a
          single <Image> can't art-direct). Both are `priority`, so Next emits a
          preload for each. Without device-scoped `sizes`, every device would
          download BOTH backgrounds at full 100vw — e.g. mobile pulling the heavy
          desktop image it never shows. Scope `sizes` so the off-breakpoint image
          preloads only the smallest candidate (~1px → 320w) instead.
        */}
        <Image
          alt={String(
            bgSlide.mobile_alt_image?.trim() ||
              bgSlide.alt_image ||
              "banner-bg",
          )}
          src={bgMobileSrc || bgDesktopSrc}
          fill
          priority
          fetchPriority="high"
          className="object-cover object-center min-[1024px]:hidden"
          sizes="(min-width: 1024px) 1px, 100vw"
        />
        <Image
          alt={String(bgSlide.alt_image || "banner-bg")}
          src={bgDesktopSrc || bgMobileSrc}
          fill
          priority
          fetchPriority="high"
          className="hidden object-cover object-center min-[1024px]:block"
          sizes="(max-width: 1023px) 1px, 100vw"
        />
      </div>

      {/* Mobile scrim. Minimal centred slides (title + accreditation only) use the
          light desktop tint so the headline reads in its desktop colours; all other
          mobile slides keep the dark scrim for white-on-photo legibility. */}
      {mobileLight ? (
        <div
          className="pointer-events-none absolute inset-0 z-[1] min-[1024px]:hidden"
          style={{ backgroundColor: "#e5e5f98c" }}
          aria-hidden
        />
      ) : (
        <>
          <div className="pointer-events-none absolute inset-0 z-[1] bg-black/35 min-[1024px]:hidden" />
          <div
            className="pointer-events-none absolute inset-0 z-[1] bg-gray-800/40 min-[1024px]:hidden"
            aria-hidden
          />
        </>
      )}
      {/* Desktop: hero background_color tint */}
      <div
        className="pointer-events-none absolute inset-0 z-[1] hidden min-[1024px]:block"
        style={{ backgroundColor: "#e5e5f98c" }}
        aria-hidden
      />

      {/* Right gutter — design: width calc(50% - 557px) for 1114px content max */}
      {hasAccentStrip ? (
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-[2] hidden min-[1024px]:block"
          style={{
            width: `calc(50% - ${rightGutterHalfPx}px)`,
            minWidth: 0,
            backgroundColor: accentStripColor,
          }}
          aria-hidden
        />
      ) : null}

      <div
        className="relative z-10 mx-auto flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-x-hidden overflow-y-visible px-0 min-[1024px]:overflow-x-clip"
        style={{ maxWidth: contentMaxPx, width: "100%" }}
      >
        <div
          className={cn(
            "flex h-full min-w-0",
            n > 1 && "transition-transform duration-700 ease-in-out",
          )}
          style={
            n > 1
              ? {
                  width: `${n * 100}%`,
                  transform: `translateX(-${(index * 100) / n}%)`,
                }
              : { width: "100%" }
          }
        >
          {slides.map((slide, slideIdx) => {
            const programs = parsePrograms(slide.programs_json);
            const accent = slide.title_line1_accent?.trim();
            const line1 = slide.title_line1?.trim() ?? "";
            const remainder =
              accent && line1 ? line1.replace(accent, "").trim() : "";

            const accC = slide.title_line1_accent_color?.trim();
            const line1C = slide.title_line1_color?.trim();
            const line2C = slide.title_line2_color?.trim();
            const subC = slide.subtitle_color?.trim();

            const slidePlain = slide as Record<string, unknown>;
            const mergeTitleLines = isCmsShowFlagOn(
              slide.merge_title_lines ?? slidePlain.mergeTitleLines,
              false,
            );
            const line2Str = slide.title_line2?.trim() ?? "";
            const titleLine2Separate = Boolean(line2Str) && !mergeTitleLines;

            const slideAcc = accLogosFromSlide(slide);
            const logosForSlide =
              slideAcc.length > 0
                ? slideAcc
                : !anySlideHasOwnAcc
                  ? accLogosGlobal
                  : [];
            const showAccRow = logosForSlide.length > 0;

            const hasPrograms = programs.length > 0;

            /**
             * Minimal slide: only a title (+ optional accreditation logos), no
             * programmes and no action row. Production centres this content
             * horizontally and vertically on desktop instead of pinning it to
             * the left gutter.
             */
            const centerContent = !hasPrograms && !showActionRow;

            /**
             * Action row (Apply Now + Download Brochure + deadline). Rendered in
             * the programme-buttons slot when a slide has no programmes, otherwise
             * pinned to the bottom alongside the accreditation logos.
             */
            const actionRow = showActionRow ? (
              <div
                className="mb-3 flex w-full min-w-0 max-w-full flex-col items-center justify-center gap-3.5 min-[1024px]:mb-0 min-[1024px]:flex-row min-[1024px]:flex-nowrap min-[1024px]:items-center min-[1024px]:justify-start min-[1024px]:gap-4"
                style={
                  {
                    ["--banner-accent" as string]: button_color,
                  } as CSSProperties
                }
              >
                {showPrimaryCta ? (
                  primaryOpensLeadPopup ? (
                    <Button
                      type="button"
                      classType="ghost"
                      className="group focus-visible:ring-ring inline-flex h-11 w-full max-w-[240px] shrink-0 flex-row items-center justify-center gap-[10px] rounded-md border px-3 py-[6px] !text-heading-6 shadow-sm min-[768px]:h-auto min-[768px]:w-[186px] min-[768px]:max-w-none min-[768px]:gap-2.5 min-[768px]:px-[18px] min-[768px]:py-2 min-[768px]:!text-heading-6"
                      style={{
                        backgroundColor: button_color,
                        color: apply_now_color,
                        borderColor: button_color,
                      }}
                      onClick={handleApplyNowClick}
                    >
                      {apply_now_text}
                      <span className="size-6 shrink-0">
                        <ArrowUpRight className="size-6 transition-transform duration-300 group-hover:rotate-[45deg]" />
                      </span>
                    </Button>
                  ) : (
                    <Link
                      href={ctaHref}
                      className="group inline-flex h-11 w-full max-w-[240px] shrink-0 flex-row items-center justify-center gap-[10px] rounded-md border px-3 py-[6px] !text-heading-6 shadow-sm min-[768px]:h-auto min-[768px]:w-[186px] min-[768px]:max-w-none min-[768px]:gap-2.5 min-[768px]:px-[18px] min-[768px]:py-2 min-[768px]:!text-heading-6"
                      style={{
                        backgroundColor: button_color,
                        color: apply_now_color,
                        borderColor: button_color,
                      }}
                      onClick={() =>
                        ctaClick({
                          section: "banner_4",
                          component: "hero_apply_now_link",
                          label: apply_now_text?.trim() || "apply_now",
                          step: "1",
                          entity: "banner_4",
                          parentEntity: "banner_4",
                        })
                      }
                    >
                      {apply_now_text}
                      <span className="size-6 shrink-0">
                        <ArrowUpRight className="size-6 transition-transform duration-300 group-hover:rotate-[45deg]" />
                      </span>
                    </Link>
                  )
                ) : null}
                {showDownloadBrochure ? (
                  brochureOpensLeadPopup ? (
                    <Button
                      type="button"
                      classType="ghost"
                      className={cn(
                        "focus-visible:ring-ring inline-flex h-11 w-full max-w-[240px] shrink-0 items-center justify-center gap-2 rounded-md border border-solid border-white bg-transparent py-2 px-[10px] !text-heading-7 shadow-sm min-[768px]:h-auto min-[768px]:max-w-none min-[768px]:w-auto min-[1024px]:border-[color:var(--banner-accent)] min-[1024px]:px-3 min-[1024px]:!text-heading-6 min-[1024px]:leading-5",
                        "text-white min-[1024px]:text-[color:var(--banner-accent)]",
                      )}
                      onClick={handleDownloadBrochure}
                    >
                      {download_brochure_text}
                      <ArrowDown
                        className="size-5 shrink-0 min-[1024px]:size-6"
                        strokeWidth={2}
                        aria-hidden
                      />
                    </Button>
                  ) : (
                    <Link
                      href={directBrochureHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "inline-flex h-11 w-full max-w-[240px] shrink-0 items-center justify-center gap-2 rounded-md border border-solid border-white bg-transparent py-2 px-[10px] !text-heading-7 shadow-sm min-[768px]:h-auto min-[768px]:max-w-none min-[768px]:w-auto min-[1024px]:border-[color:var(--banner-accent)] min-[1024px]:px-3 min-[1024px]:!text-heading-6 min-[1024px]:leading-5",
                        "text-white min-[1024px]:text-[color:var(--banner-accent)]",
                      )}
                      onClick={() =>
                        ctaClick({
                          section: "banner_4",
                          component: "download_brochure_link",
                          label: download_brochure_text,
                          step: "1",
                          entity: "banner_4",
                          parentEntity: "banner_4",
                        })
                      }
                    >
                      {download_brochure_text}
                      <ArrowDown
                        className="size-5 shrink-0 min-[1024px]:size-6"
                        strokeWidth={2}
                        aria-hidden
                      />
                    </Link>
                  )
                ) : null}
                {showApplicationDeadline ? (
                  <p
                    className={cn(
                      "max-w-full text-center text-body-3 italic min-[1024px]:min-w-0 min-[1024px]:text-left",
                      "text-white min-[1024px]:text-[color:var(--banner-accent)]",
                    )}
                  >
                    {application_deadline_text}
                  </p>
                ) : null}
              </div>
            ) : null;

            return (
              <div
                key={String(slide.id ?? slide.image ?? slideIdx)}
                className={cn(
                  "relative flex h-full min-w-0 shrink-0 flex-col min-[1024px]:flex-row",
                  n === 1 && "w-full max-w-full",
                )}
                style={n === 1 ? undefined : { width: `${100 / n}%` }}
              >
                <div className="flex min-h-0 min-w-0 w-full max-w-full min-[1024px]:flex-[3]">
                  <div
                    className={cn(
                      "flex h-full min-h-0 min-w-0 w-full max-w-full flex-col items-center justify-between pt-24 pb-5 px-5",
                      "min-[1024px]:gap-10 min-[1024px]:pt-16 min-[1024px]:pb-10",
                      centerContent
                        ? // Minimal slide: left-aligned + vertically centred, using the full
                          // ~668px column width (matches production) so the headline doesn't
                          // wrap early ("...to the" stays on line 1).
                          "min-[1024px]:items-start min-[1024px]:justify-center min-[1024px]:pl-10 min-[1024px]:w-[668px]"
                        : "min-[1024px]:items-start min-[1024px]:pl-10 min-[1024px]:w-[614px]",
                      "min-[768px]:w-fit",
                    )}
                  >
                    <div
                      className={cn(
                        "flex min-h-0 w-full max-w-full flex-1 flex-col z-10",
                        centerContent && "min-[1024px]:justify-center",
                      )}
                    >
                      <div
                        className={cn(
                          "z-10 flex min-h-0 min-w-0 w-full max-w-full flex-none flex-col [box-sizing:border-box]",
                          centerContent
                            ? "min-[1024px]:w-full"
                            : "min-[1024px]:w-[554px]",
                        )}
                      >
                      {showHeroInfoBar ? (
                        <div className="mb-3 flex w-full flex-row items-center justify-center min-[1024px]:justify-start">
                          <p
                            className={cn(
                              "w-fit max-w-full px-2.5 py-0 text-sm font-normal leading-[1.5] text-[#2A2A2A] bg-[rgb(253,225,209)]",
                              "[overflow-wrap:break-word] [word-break:normal]",
                            )}
                          >
                            {hero_info_bar_text}
                          </p>
                        </div>
                      ) : null}
                      <h1
                        className={cn(
                          "block w-full min-w-0 max-w-full break-words text-center font-bold leading-snug [overflow-wrap:break-word] [word-break:normal] max-[1024px]:text-balance min-[1024px]:inline-flex min-[1024px]:max-w-none min-[1024px]:text-[35px] min-[1024px]:leading-snug min-[1024px]:text-black",
                          // Even when the minimal-slide block is centred, the title
                          // text stays left-aligned so a wrapped 2nd line starts under
                          // the 1st line's left edge (block centred via parent items-center).
                          "min-[1024px]:text-left",
                          titleMobileSize,
                          mobileLight ? "text-black" : "text-white",
                        )}
                      >
                        <span className="inline-block w-full max-w-full px-0 min-[1024px]:inline min-[1024px]:w-fit">
                          {accent ? (
                            <>
                              <span
                                className={cn(
                                  !accC && accentDefaultCls,
                                  accC &&
                                    "[color:var(--bn4-l1a-m)] min-[1024px]:[color:var(--bn4-l1a-d)]",
                                )}
                                style={
                                  accC
                                    ? ({
                                        ["--bn4-l1a-m" as string]:
                                          resolveMobileTitleColor(accC),
                                        ["--bn4-l1a-d" as string]: accC,
                                      } as CSSProperties)
                                    : undefined
                                }
                              >
                                {accent}
                              </span>
                              {remainder ? (
                                <span
                                  className={cn(
                                    !line1C && bodyDefaultCls,
                                    line1C &&
                                      "[color:var(--bn4-l1r-m)] min-[1024px]:[color:var(--bn4-l1r-d)]",
                                  )}
                                  style={
                                    line1C
                                      ? ({
                                          ["--bn4-l1r-m" as string]:
                                            resolveMobileTitleColor(line1C),
                                          ["--bn4-l1r-d" as string]: line1C,
                                        } as CSSProperties)
                                      : undefined
                                  }
                                >
                                  {" "}
                                  {remainder}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span
                              className={cn(
                                !(line1C || accC) && accentDefaultCls,
                                (line1C || accC) &&
                                  "[color:var(--bn4-l1-m)] min-[1024px]:[color:var(--bn4-l1-d)]",
                              )}
                              style={
                                line1C || accC
                                  ? ({
                                      ["--bn4-l1-m" as string]:
                                        resolveMobileTitleColor(line1C || accC),
                                      ["--bn4-l1-d" as string]: line1C || accC,
                                    } as CSSProperties)
                                  : undefined
                              }
                            >
                              {line1}
                            </span>
                          )}
                          {mergeTitleLines && line2Str ? (
                            <>
                              {" "}
                              <span
                                className={cn(
                                  mobileLight && line2C
                                    ? "[color:var(--bn4-l2-m)]"
                                    : mobileLight
                                      ? "text-black"
                                      : "text-white",
                                  line2C
                                    ? "min-[1024px]:[color:var(--bn4-line2-color)]"
                                    : "min-[1024px]:text-black",
                                )}
                                style={
                                  line2C
                                    ? ({
                                        ["--bn4-l2-m" as string]:
                                          resolveMobileTitleColor(line2C),
                                        ["--bn4-line2-color" as string]:
                                          line2C,
                                      } as CSSProperties)
                                    : undefined
                                }
                              >
                                {line2Str}
                              </span>
                            </>
                          ) : null}
                        </span>
                      </h1>
                      {titleLine2Separate ? (
                        <h2
                          className={cn(
                            "mt-2 block w-full min-w-0 max-w-full break-words text-center font-bold leading-snug [overflow-wrap:break-word] [word-break:normal] min-[1024px]:inline-flex min-[1024px]:max-w-none min-[1024px]:w-full min-[1024px]:text-[35px] min-[1024px]:leading-snug",
                            "min-[1024px]:text-left",
                            titleMobileSize,
                            mobileLight && line2C
                              ? "[color:var(--bn4-l2-m)]"
                              : mobileLight
                                ? "text-black"
                                : "text-white",
                            line2C
                              ? "min-[1024px]:[color:var(--bn4-line2-color)]"
                              : "min-[1024px]:text-black",
                          )}
                          style={
                            line2C
                              ? ({
                                  ["--bn4-l2-m" as string]:
                                    resolveMobileTitleColor(line2C),
                                  ["--bn4-line2-color" as string]: line2C,
                                } as CSSProperties)
                              : undefined
                          }
                        >
                          <span className="inline-block leading-[1.4] w-full max-w-full px-[2px] min-[1024px]:inline min-[1024px]:w-fit min-[1024px]:px-0">
                            {line2Str}
                          </span>
                        </h2>
                      ) : null}
                      {slide.subtitle ? (
                        <p
                          className={cn(
                            "mt-2.5 block w-full min-w-0 max-w-full break-words text-center text-body-3 leading-relaxed [overflow-wrap:break-word] [word-break:normal] min-[1024px]:inline-flex min-[1024px]:max-w-none min-[1024px]:w-[554px]",
                            "min-[1024px]:text-left",
                            subC
                              ? cn(
                                  mobileLight
                                    ? "![color:var(--bn4-subtitle-color)]"
                                    : "!text-white",
                                  "min-[1024px]:![color:var(--bn4-subtitle-color)]",
                                )
                              : cn(
                                  mobileLight ? "!text-gray-400" : "!text-white",
                                  "min-[1024px]:!text-gray-400",
                                ),
                          )}
                          style={
                            subC
                              ? ({
                                  ["--bn4-subtitle-color" as string]: subC,
                                } as CSSProperties)
                              : undefined
                          }
                        >
                          <span className="inline-block w-full max-w-full px-[2px] min-[1024px]:inline min-[1024px]:w-fit min-[1024px]:px-0">
                            {slide.subtitle}
                          </span>
                        </p>
                      ) : null}
                      </div>

                      {hasPrograms ? (
                        <div className="w-[300px] self-center mt-5 min-[768px]:self-start min-[768px]:w-[500px] min-[768px]:mt-8">
                          <div
                            className={cn(
                              "!py-0 !w-full",
                              programs.length > 5
                                ? "flex flex-wrap gap-2 justify-center min-[1024px]:justify-start"
                                : "flex gap-2 justify-center min-[1024px]:justify-start",
                            )}
                          >
                            {programs.map((p) => (
                              <Link
                                key={p.href + p.label}
                                href={p.href}
                                className={programLinkClass}
                              >
                                {p.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : actionRow ? (
                        /* No programme buttons: the action row takes their slot. */
                        <div className="mt-5 w-full self-stretch min-[768px]:mt-8">
                          {actionRow}
                        </div>
                      ) : null}

                    {(hasPrograms && showActionRow) || showAccRow ? (
                      <div
                        className={cn(
                          "mt-3.5 flex w-full min-w-0 max-w-full shrink-0 flex-col self-stretch gap-0 min-[1024px]:gap-6",
                          centerContent
                            ? "min-[1024px]:mt-6"
                            : "min-[1024px]:mt-auto",
                        )}
                      >
                        {/* When the slide has programmes the action row stays pinned to the bottom; without programmes it renders up in the programme slot instead. */}
                        {hasPrograms ? actionRow : null}

                        {showAccRow ? (
                          <div
                            className={cn(
                              "mb-5 flex w-full min-w-0 max-w-full flex-row flex-wrap justify-center gap-0 self-stretch px-0 pt-6 min-[1024px]:mx-0 min-[1024px]:mb-5 min-[1024px]:max-w-none min-[1024px]:flex-nowrap min-[1024px]:pt-0 min-[1024px]:justify-start",
                              "max-[1024px]:mt-8 max-[1024px]:pt-2",
                              showHeroInfoBar &&
                                "max-[1024px]:mt-12 max-[1024px]:pt-4",
                            )}
                          >
                            {logosForSlide.map((logo, i) => (
                              <div
                                key={`${String(slide.id ?? slideIdx)}-${String(logo.id ?? logo.image)}-${i}`}
                                className={cn(
                                  "relative h-[60px] w-[min(100%,146px)] max-w-[146px] shrink-0 border-black bg-white shadow-md",
                                  i > 0 && "border-l-[1px]",
                                  i >= 2 &&
                                    "border-t-[1px] border-black min-[768px]:border-l-[1px] min-[768px]:border-t-0 min-[1024px]:border-l-[1px] min-[1024px]:border-t-0",
                                )}
                              >
                                <Image
                                  alt={logo.alt_image || ""}
                                  src={logo.image || ""}
                                  fill
                                  className="object-contain bg-white"
                                  sizes="146px"
                                />
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    </div>
                  </div>
                </div>

                {/* Right column: layout spacer that keeps the 3:2 split (so left content stays in the left 60%). The solid band colour is only painted when an accent colour is set. */}
                <div className="hidden h-full min-[1024px]:flex min-[1024px]:flex-[2] min-[1024px]:items-end min-[1024px]:justify-end">
                  {hasAccentStrip ? (
                    <div
                      className="relative h-full w-[50%]"
                      style={{ backgroundColor: accentStripColor }}
                    />
                  ) : null}
                </div>

                {/* Hero: center on flex 3|2 seam (60%), large — not inside navy half so it straddles light + solid */}
                {slide.hero_right_image ? (
                  <div className="pointer-events-none absolute inset-y-0 left-[80%] z-[4] hidden -translate-x-1/2 overflow-visible min-[1024px]:flex min-[1024px]:items-center min-[1024px]:justify-center">
                    <Image
                      alt={slide.alt_hero || "banner"}
                      src={slide.hero_right_image}
                      width={432}
                      height={432}
                      sizes="(max-width: 1023px) 0px, 560px"
                      priority={slideIdx === 0}
                      fetchPriority={slideIdx === 0 ? "high" : "auto"}
                      className="h-[432px] w-[432px] max-h-[560px] max-w-[560px]"
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {n > 1 ? (
        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 transform gap-3">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={cn(
                "h-3 w-3 rounded-full transition-all duration-300 ease-in-out",
                i === index ? "bg-primary" : "bg-gray-300",
              )}
              style={
                i === index ? { backgroundColor: carousel_color } : undefined
              }
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default HeroCarousel;
